import { ChangeDetectionStrategy, Component, isDevMode, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { IProduct } from 'src/app/models/products/product';
import { ProductResult } from 'src/app/models/products/product-result';
import { ProductTouch } from 'src/app/models/products/actions/ingest/productTouch';
import { ProductReceival } from 'src/app/models/products/actions/receive/productReceival';
import { ProductsService } from 'src/app/services/products.service';

export interface LifecycleNode {
  readonly label: string;
  readonly sublabel?: string;
  readonly countText?: string;
  readonly link?: string;
  readonly historyAvailable: boolean;
  readonly children?: readonly LifecycleNode[];
}

type ReviewAssetType = 'shoot-assets' | 'post-assets' | 'output-assets';

export function buildReviewAssetsLink(
  batchId: string,
  productId: string,
  touch: Pick<ProductTouch, 'id' | 'masterSuiteTouchId'>,
  assetType: ReviewAssetType,
): string {
  return `/batches/${batchId}/products/${productId}/productTouchAsset/${touch.id}/masterSuiteTouchAsset/${touch.masterSuiteTouchId}/${assetType}`;
}

function buildBatchNode(product: IProduct): LifecycleNode {
  return {
    label: 'Batch',
    sublabel: product.batchName,
    link: `/batches/${product.batchId}`,
    historyAvailable: false,
  };
}

function buildMasterSuiteNode(product: IProduct): LifecycleNode {
  return product.masterSuiteId
    ? {
        label: 'Master Suite',
        sublabel: product.masterSuiteName ?? undefined,
        link: `/mastersuites/${product.masterSuiteId}/update`,
        historyAvailable: false,
      }
    : {
        label: 'Master Suite',
        sublabel: 'Not assigned',
        historyAvailable: false,
      };
}

function buildSettingsVariantNode(product: IProduct): LifecycleNode {
  return product.settingsVariantId
    ? {
        label: 'Settings Variant',
        sublabel: product.settingsVariantName ?? undefined,
        historyAvailable: false,
      }
    : {
        label: 'Settings Variant',
        sublabel: 'Not assigned',
        historyAvailable: false,
      };
}

function buildEnhancementVariantNode(product: IProduct): LifecycleNode {
  return {
    label: 'Enhancement Variant',
    sublabel: product.masterSuiteEnhancementVariantId ? 'Assigned' : 'Not assigned',
    historyAvailable: false,
  };
}

function buildReceivalChild(receival: ProductReceival): LifecycleNode {
  return {
    label: `Receival ${receival.containerId}`,
    sublabel: `${receival.quantityReceived} × ${receival.condition}`,
    historyAvailable: false,
  };
}

function buildReceivalsNode(product: IProduct): LifecycleNode {
  return {
    label: 'Receivals',
    sublabel: `${product.receivedCount} received`,
    historyAvailable: false,
    children: product.productReceivals.map(buildReceivalChild),
  };
}

function buildTouchAssetChildren(product: IProduct, touch: ProductTouch): readonly LifecycleNode[] {
  return [
    {
      label: 'Shoot Assets',
      link: buildReviewAssetsLink(product.batchId, product.id!, touch, 'shoot-assets'),
      historyAvailable: false,
    },
    {
      label: 'Post Assets',
      link: buildReviewAssetsLink(product.batchId, product.id!, touch, 'post-assets'),
      historyAvailable: false,
    },
    {
      label: 'Output Assets',
      link: buildReviewAssetsLink(product.batchId, product.id!, touch, 'output-assets'),
      historyAvailable: false,
    },
  ];
}

function buildTouchNode(product: IProduct, touch: ProductTouch): LifecycleNode {
  return {
    label: touch.masterSuiteTouchName,
    sublabel: `Touch ${touch.masterSuiteTouchIndex + 1}`,
    historyAvailable: false,
    children: buildTouchAssetChildren(product, touch),
  };
}

function buildTouchesNode(product: IProduct): LifecycleNode {
  return {
    label: 'Product Touches',
    countText: `${product.ingestedTouchCount} / ${product.expectedTouchCount} ingested`,
    historyAvailable: false,
    children: product.productTouches.map((touch) => buildTouchNode(product, touch)),
  };
}

function buildLabelDataNode(product: IProduct): LifecycleNode {
  return {
    label: 'Label Data',
    sublabel: product.productLabelDataGenerated ? 'Generated' : 'Not generated',
    historyAvailable: false,
  };
}

function buildCollectionsNode(product: IProduct): LifecycleNode {
  return product.releasedInCollection
    ? {
        label: 'Collections',
        sublabel: 'Released',
        link: '/collections',
        historyAvailable: false,
      }
    : {
        label: 'Collections',
        sublabel: 'Not released',
        historyAvailable: false,
      };
}

export function buildLifecycleTree(product: IProduct): readonly LifecycleNode[] {
  return [
    buildBatchNode(product),
    buildMasterSuiteNode(product),
    buildSettingsVariantNode(product),
    buildEnhancementVariantNode(product),
    buildReceivalsNode(product),
    buildTouchesNode(product),
    buildLabelDataNode(product),
    buildCollectionsNode(product),
  ];
}

@Component({
  selector: 'app-lifecycle-page',
  templateUrl: './lifecycle-page.component.html',
  styleUrls: ['./lifecycle-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: false,
})
export class LifecyclePageComponent implements OnInit {
  product: IProduct | null = null;
  nodes: readonly LifecycleNode[] = [];
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly productsService: ProductsService,
  ) {}

  ngOnInit(): void {
    const productId = this.route.snapshot.paramMap.get('productId');
    if (!productId) {
      if (isDevMode()) console.log('[DEV] [LifecyclePageComponent] no productId in route, skipping load');
      return;
    }
    if (isDevMode()) console.log('[DEV] [LifecyclePageComponent] loading lifecycle for product', productId);
    this.productsService
      .detailsProduct(productId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: ProductResult) => {
        this.product = result.product;
        this.nodes = buildLifecycleTree(result.product);
        if (isDevMode()) {
          console.log('[DEV] [LifecyclePageComponent] loaded product', {
            productId,
            barcode: result.product.barcode,
            reference: result.product.reference,
            topLevelNodeCount: this.nodes.length,
          });
        }
      });
  }

  onBack(): void {
    const batchId = this.route.snapshot.paramMap.get('batchId');
    const productId = this.route.snapshot.paramMap.get('productId');
    this.router.navigateByUrl(`/batches/${batchId}/products/${productId}`);
  }

  trackByLabel(_index: number, node: LifecycleNode): string {
    return node.label;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
